import {
  Attribute,
  AttributeFormatting,
  Layer,
  LayerBase,
  LayerProperties,
  LayerProperty,
  LayerPropertyValue,
  PartialBy,
} from "types";

import {
  LayerPropertyResponse,
  AttributeFormattingResponse,
  AttributeResponse,
  LayerBaseResponse,
  LayerResponse,
} from "types/backend";

function deserializeNumberFormatting(
  formatting: AttributeFormattingResponse
): AttributeFormatting {
  const { noDataValue, options, ...restFormatting } = formatting;
  return {
    ...restFormatting,
    noDataValue: {
      ...noDataValue,
      min: noDataValue?.min || "",
      max: noDataValue?.max || "",
    },
    options: {
      maximumFractionDigits:
        options.maximumFractionDigits !== null
          ? options.maximumFractionDigits
          : undefined,
      maximumSignificantDigits:
        options.maximumSignificantDigits !== null
          ? options.maximumSignificantDigits
          : undefined,
      minimumFractionDigits:
        options.minimumFractionDigits !== null
          ? options.minimumFractionDigits
          : undefined,
      minimumIntegerDigits:
        options.minimumIntegerDigits !== null
          ? options.minimumIntegerDigits
          : undefined,
      minimumSignificantDigits:
        options.minimumSignificantDigits !== null
          ? options.minimumSignificantDigits
          : undefined,
      notation: options.notation || undefined,
      signDisplay: options.signDisplay || undefined,
      unit: options.unit || undefined,
      unitDisplay: options.unitDisplay || undefined,
    },
  };
}

export function deserializeAttributes(attribute: AttributeResponse): Attribute {
  const { formatting, layers, grouping, order, ...restAttribute } = attribute;
  return {
    ...restAttribute,
    layers: layers || "",
    grouping: grouping || false,
    order: order || null,
    formatting:
      formatting !== null ? deserializeNumberFormatting(formatting) : "",
  };
}

function deserializeURL(url: string): string {
  const { protocol, hostname } = window.location;
  if (hostname === "localhost")
    return url
      .replaceAll(/https?:\/\/[a-zA-Z0-9]*\.marvin(test)?\.vito\.be/g, "")
      .replace(/https?:\/\//g, `${protocol}//`);
  return url.replace(/https?:\/\//g, `${protocol}//`);
}

function deserializeLayerProperties(
  property: LayerPropertyResponse,
  layerProperties: LayerProperties[]
): LayerProperty {
  const { value, ...restProperty } = property;
  const matched = layerProperties.reduce(
    (matched: LayerPropertyValue | null, prop: LayerProperties) =>
      prop.values.find((val) => val.name === value) || matched,
    null
  );
  if (matched) return { ...restProperty, value: matched };
  return {
    ...restProperty,
    value: { id: 0, order: 999, disableSettingsVariableIds: [], name: "" },
  };
}

export function deserializeLayer(
  response: LayerResponse,
  layerProperties: LayerProperties[]
): Layer {
  const { wmsParams, attributes, ...details } = response;

  return {
    ...details,
    wms: {
      ...wmsParams,
      url: deserializeURL(wmsParams?.url || ""),
    },
    attributes: (attributes || []).map(deserializeAttributes),
    sourceType: "",
    minZoom: details.minZoom || 0,
    maxZoom: details.maxZoom || 0,
    legend: { title: "", type: "", entity: "", legendItems: [] },
    properties: (response.layerProperties || []).map((propery) =>
      deserializeLayerProperties(propery, layerProperties)
    ),
  };
}

export function deserializeLayerBase(
  response: LayerBaseResponse,
  allLayerProperties: LayerProperties[]
): LayerBase {
  const { layerProperties, ...restResponse } = response;
  return {
    ...restResponse,
    properties: (layerProperties || []).map((propery) =>
      deserializeLayerProperties(propery, allLayerProperties)
    ),
  };
}

function serializeLayerProperties(
  property: LayerProperty
): LayerPropertyResponse {
  const { value, ...restProperty } = property;

  return {
    ...restProperty,
    value: value.name,
  };
}

export function serializeLayer(
  layer: PartialBy<Layer, "id">
): PartialBy<LayerResponse, "id"> {
  const { properties, attributes, wms, sourceType, legend, ...details } = layer;

  const result = {
    ...details,
    attribution: "",
    attributes: (attributes || []).map(serializeAttributes),
    wmsParams: {
      ...wms,
      url: wms.url.replace(/^(http:|https:)/g, "https:"), //force same protocol, fixes Mixed Content warning, important? NO! But everyone should use https!
      styles: wms.styles || "",
    },
    layerProperties: (properties || []).map(serializeLayerProperties),
  };

  return result;
}

export function serializeAttributes(attribute: Attribute): AttributeResponse {
  const { formatting, layers, ...restAttribute } = attribute;

  return {
    ...restAttribute,
    layers: layers || null,
    formatting:
      formatting !== "" ? serializeNumberFormatting(formatting) : null,
  };
}

function serializeNumberFormatting(
  formatting: AttributeFormatting
): AttributeFormattingResponse {
  const { noDataValue, ...restFormatting } = formatting;
  return {
    ...restFormatting,
    noDataValue: {
      ...noDataValue,
      min: noDataValue?.min || null,
      max: noDataValue?.max || null,
    },
  };
}
